import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { urlConstants } from '../../../constants/urlConstants';

export interface ExpoSiteVisitItem {
  _id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phoneNumber: string;
  primaryMobCountryCode?: string;
  alternateNumber?: string;
  secondaryMobCountryCode?: string;
  currentResidentArea?: string;
  cityOfResidence?: string;
  countryOfResidence?: string;
  pinCode?: string;
  zipPostalCode?: string;
  zone?: string;
  project?: {
    _id: string;
    name: string;
  } | string;
  projectName?: string;
  customerType?: string;
  bookedCustomer?: boolean;
  unitNumber?: string;
  purposeOfVisit?: string;
  propertyLookingFor?: string;
  preferredInvestment?: string;
  homeType?: string;
  homeTypeCustom?: string;
  bedroomPreference?: string;
  familySize?: string;
  monthlySalaryRange?: string;
  preferredUnit?: any;
  preferredBudgetRange?: any;
  minPrice?: string;
  maxPrice?: string;
  companyName?: string;
  designation?: string;
  sourceOfInformation?: {
    _id: string;
    source: string;
  } | string;
  primarySource?: string;
  secondarySource?: string;
  source?: string;
  tertiarySource?: string;
  flsEmail?: string;
  ageRange?: {
    _id: string;
    ageRange: string;
  } | string;
  occupation?: {
    _id: string;
    occupation: string;
  } | string;
  preferredSize?: {
    _id: string;
    size: string;
  } | string;
  purposeOfPurchase?: {
    _id: string;
    purpose: string;
  } | string;
  isOtpVerified?: boolean;
  otpVerifiedAt?: string;
  isNRI?: number;
  recordStatus?: string;
  salesforceApiStatus: 'Pending' | 'Success' | 'Failed';
  salesforceApiPayload?: any;
  salesforceApiResponse?: any;
  salesforceApiCalledAt?: string;
  salesforceApiError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExpoSiteVisitListResponse {
  statusCode: number;
  success: boolean;
  message?: string;
  data: ExpoSiteVisitItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  stats?: {
    total: number;
    successCount: number;
    failedCount: number;
    pendingCount: number;
  };
}

export const expoSiteVisitApi = createApi({
  reducerPath: 'expoSiteVisitApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config().BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('cw-token');
      if (token && token !== 'null' && token !== 'undefined') {
        headers.set('Authorization', `Token ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['expoSiteVisit'],
  endpoints: (builder) => ({
    // 1. Submit Expo Site Visit Form (Open / Public)
    submitExpoSiteVisit: builder.mutation<
      { statusCode: number; success: boolean; message: string; data: ExpoSiteVisitItem; salesforceStatus: string },
      any
    >({
      query: (body) => ({
        url: urlConstants.EXPO_SITE_VISIT_SUBMIT,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['expoSiteVisit'],
    }),

    // 2. Admin: Get paginated records with filters
    getExpoSiteVisits: builder.query<
      ExpoSiteVisitListResponse,
      {
        search?: string;
        customerName?: string;
        mobile?: string;
        project?: string;
        salesforceApiStatus?: string;
        startDate?: string;
        endDate?: string;
        page?: number;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: urlConstants.EXPO_SITE_VISIT_LIST,
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['expoSiteVisit'],
    }),

    // 3. Admin: Get record details by ID
    getExpoSiteVisitById: builder.query<{ statusCode: number; success: boolean; data: ExpoSiteVisitItem }, string>({
      query: (id) => ({
        url: `${urlConstants.EXPO_SITE_VISIT_GET}/${id}`,
        method: 'GET',
      }),
      providesTags: ['expoSiteVisit'],
    }),

    // 4. Admin: Export records matching filters
    exportExpoSiteVisits: builder.query<
      { statusCode: number; success: boolean; data: ExpoSiteVisitItem[]; count: number },
      {
        search?: string;
        customerName?: string;
        mobile?: string;
        project?: string;
        salesforceApiStatus?: string;
        startDate?: string;
        endDate?: string;
      } | void
    >({
      query: (params) => ({
        url: urlConstants.EXPO_SITE_VISIT_EXPORT,
        method: 'GET',
        params: params || {},
      }),
    }),

    // 5. Admin: Lazy query for export on demand
    lazyExportExpoSiteVisits: builder.mutation<
      { statusCode: number; success: boolean; data: ExpoSiteVisitItem[]; count: number },
      any
    >({
      query: (params) => ({
        url: urlConstants.EXPO_SITE_VISIT_EXPORT,
        method: 'GET',
        params: params || {},
      }),
    }),

    // 6. Admin: Retry Salesforce call for a record
    retryExpoSalesforceApi: builder.mutation<
      { statusCode: number; success: boolean; message: string; data: ExpoSiteVisitItem },
      string
    >({
      query: (id) => ({
        url: `${urlConstants.EXPO_SITE_VISIT_RETRY_SF}/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['expoSiteVisit'],
    }),

    // 7. Get ongoing projects and user emails from Salesforce
    getExpoOngoingProjects: builder.query<
      { statusCode: number; success: boolean; data: { projectNames: string[]; userEmails: string[] } },
      void
    >({
      query: () => ({
        url: urlConstants.EXPO_SITE_VISIT_ONGOING_PROJECTS,
        method: 'GET',
      }),
      providesTags: ['expoSiteVisit'],
    }),
  }),
});

export const {
  useSubmitExpoSiteVisitMutation,
  useGetExpoSiteVisitsQuery,
  useGetExpoSiteVisitByIdQuery,
  useExportExpoSiteVisitsQuery,
  useLazyExportExpoSiteVisitsMutation,
  useRetryExpoSalesforceApiMutation,
  useGetExpoOngoingProjectsQuery,
} = expoSiteVisitApi;
